import { createParser } from "eventsource-parser";

import type { Message } from './App';
import { apiClient, getAuthHeader } from './apiClient';
import { retrieveAccessTokenFromBackend } from './security';

async function getMessagesWithTokenRefreshThrowOnError(chatId: string, content: string, file?: File | null) : Promise<Response> {
    const url = `${import.meta.env.VITE_API_URL}/api/chats/${chatId}/messages/`;

    // Helper to perform the POST with given headers
    async function fetchWithCredentials(headers: HeadersInit): Promise<Response> {
        const finalHeaders = { ...headers } as Record<string, string>;
        let body: BodyInit;

        if (file) {
            const formData = new FormData();
            formData.append('content', content);
            formData.append('files', file);
            formData.append('tool_calls', JSON.stringify([]));
            formData.append('tool_call_id', '');
            
            body = formData;
            // Omit Content-Type to let the browser set the multipart boundary
            delete finalHeaders['Content-Type'];
        } else {
            body = JSON.stringify({ content, tool_calls: [], tool_call_id: '' });
        }

        return fetch(url, {
            method: 'POST',
            headers: finalHeaders,
            credentials: 'include',
            body,
        });
    }

    // First attempt with the current auth header (may use a cached token)
    const initialHeaders: HeadersInit = {
        'Content-Type': 'application/json',
        ...(await getAuthHeader()),
    };

    let response = await fetchWithCredentials(initialHeaders);
    if (response.status === 401) {
        try {
            const freshToken = await retrieveAccessTokenFromBackend();

            if (freshToken) {
                const retryHeaders: HeadersInit = {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${freshToken}`,
                };

                response = await fetchWithCredentials(retryHeaders);
            }
        } catch {
            // If refreshing fails, we'll fall through to the !ok check below
        }
    }

    if (!response.ok) {
        // Surface quota-exhausted (402) with structured data so the
        // caller can show an upgrade modal instead of a generic error.
        if (response.status === 402) {
            let detail: Record<string, unknown> = {};
            try { detail = await response.json(); } catch { /* body might not be JSON */ }
            const err = new Error(
                (detail.detail as string) || "You've used your free tokens this month.",
            );
            (err as Error & { code?: string; status?: number }).code = 'chat_quota_exhausted';
            (err as Error & { status?: number }).status = 402;
            throw err;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    if (!response.body) {
        throw new Error('Response body is null');
    }

    return response;
}


export async function getMessagesForChat(chatId: string): Promise<Message[]> {
    const response = await apiClient.get(`/api/chats/${chatId}/messages/`);
    return response.data;
}

export async function createMessage(chatId: string, content: string): Promise<Message> {
    const response = await apiClient.post(
        `/api/chats/${chatId}/messages/`,
        { content, tool_calls: [], tool_call_id: '' },
    );
    return response.data;
}

/**
 * Post a user message and stream the assistant's response via SSE.
 *
 * Error handling:
 * - 402 (quota exhausted) is re-thrown with `code: 'chat_quota_exhausted'`
 *   so the caller can show an upgrade prompt.
 * - SSE-level `error` events from the backend are captured and re-thrown.
 * - Network / decoding errors during the read loop are caught and wrapped
 *   in a user-friendly message so the caller can show a toast.
 */
export async function createMessageThenStreamResponse(
    chatId: string,
    content: string,
    onMessagesUpdated: (messages: Message[]) => void,
    file?: File | null
): Promise<void> {

    // Capture SSE-level errors so we can re-throw them after the read loop.
    let sseError: Error | null = null;

    const parser = createParser({
        onEvent(ev) {
            const data = ev.data;
            const jsonData = JSON.parse(data);

            switch (ev.event) {
                case 'messages_update':
                    onMessagesUpdated(jsonData);
                    break;
                case 'error':
                    // Store the error — throwing inside the parser callback
                    // does not propagate to the outer async scope.
                    sseError = new Error(jsonData.message || 'Server error during streaming.');
                    break;
            }
        }
    });

    // This call handles 401 retry and 402 quota errors internally.
    const response = await getMessagesWithTokenRefreshThrowOnError(chatId, content, file);
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const decoded = decoder.decode(value, { stream: true });
            parser.feed(decoded);

            // If the backend sent an SSE error event, stop reading.
            if (sseError) break;
        }
    } catch (readErr) {
        // Network failure or stream decode error during reading.
        throw new Error('Connection lost while receiving response. Please try again.');
    } finally {
        reader.cancel();
    }

    // Surface SSE-level errors to the caller so they can show a toast.
    if (sseError) throw sseError;
}

export async function deleteChat(chatId: string): Promise<void> {
    await apiClient.delete(`/api/chats/${chatId}/`);
}

export async function renameChat(chatId: string, name: string): Promise<void> {
    await apiClient.patch(`/api/chats/${chatId}/`, { name });
}

export async function moveChatToProject(chatId: string, projectId: string | null): Promise<void> {
    await apiClient.patch(`/api/chats/${chatId}/`, { project: projectId || '' });
}
